import * as THREE from 'three'
import * as glslify from 'glslify'
import FloorMaterial from '../Materials/Floor.js'
import mipmapper from "../cine/effects/mipmapper";
import fboHelper from "../cine/helpers/fboHelper";
import shaderHelper from "../cine/helpers/shaderHelper";
import glPositionOffset from "../cine/effects/glPositionOffset";
import blueNoise from "../cine/effects/blueNoise";

export default class Floor
{
    constructor(_options)
    {
        this.update = this.update.bind(this);

        blueNoise.init(_options);

        shaderHelper.addChunk("textureBicubic", glslify(["#define GLSLIFY 1\nvec4 cubic(float v) {\n    vec4 n = vec4(1.0, 2.0, 3.0, 4.0) - v;\n    vec4 s = n * n * n;\n    float x = s.x;\n    float y = s.y - 4.0 * s.x;\n    float z = s.z - 4.0 * s.y + 6.0 * s.x;\n    float w = 6.0 - x - y - z;\n    return vec4(x, y, z, w);\n}\n\n// https://stackoverflow.com/questions/13501081/efficient-bicubic-filtering-code-in-glsl\nvec4 textureBicubic(sampler2D t, vec2 texCoords, vec2 textureSize) {\n   vec2 invTexSize = 1.0 / textureSize;\n   texCoords = texCoords * textureSize - 0.5;\n\n    vec2 fxy = fract(texCoords);\n    texCoords -= fxy;\n    vec4 xcubic = cubic(fxy.x);\n    vec4 ycubic = cubic(fxy.y);\n\n    vec4 c = texCoords.xxyy + vec2 (-0.5, 1.5).xyxy;\n\n    vec4 s = vec4(xcubic.xz + xcubic.yw, ycubic.xz + ycubic.yw);\n    vec4 offset = c + vec4 (xcubic.yw, ycubic.yw) / s;\n\n    offset *= invTexSize.xxyy;\n\n    vec4 sample0 = texture2D(t, offset.xz);\n    vec4 sample1 = texture2D(t, offset.yz);\n    vec4 sample2 = texture2D(t, offset.xw);\n    vec4 sample3 = texture2D(t, offset.yw);\n\n    float sx = s.x / (s.x + s.y);\n    float sy = s.z / (s.z + s.w);\n\n    return mix(\n       mix(sample3, sample2, sx), mix(sample1, sample0, sx)\n    , sy);\n}\n\nvec4 textureBicubic(sampler2D t, vec2 texCoords, vec2 textureSize, vec4 clampRect) {\n   vec2 invTexSize = 1.0 / textureSize;\n   texCoords = texCoords * textureSize - 0.5;\n\n    vec2 fxy = fract(texCoords);\n    texCoords -= fxy;\n    vec4 xcubic = cubic(fxy.x);\n    vec4 ycubic = cubic(fxy.y);\n\n    vec4 c = texCoords.xxyy + vec2 (-0.5, 1.5).xyxy;\n\n    vec4 s = vec4(xcubic.xz + xcubic.yw, ycubic.xz + ycubic.yw);\n    vec4 offset = c + vec4 (xcubic.yw, ycubic.yw) / s;\n\n    offset *= invTexSize.xxyy;\n\n    vec4 sample0 = texture2D(t, clamp(offset.xz, clampRect.xy, clampRect.zw));\n    vec4 sample1 = texture2D(t, clamp(offset.yz, clampRect.xy, clampRect.zw));\n    vec4 sample2 = texture2D(t, clamp(offset.xw, clampRect.xy, clampRect.zw));\n    vec4 sample3 = texture2D(t, clamp(offset.yw, clampRect.xy, clampRect.zw));\n\n    float sx = s.x / (s.x + s.y);\n    float sy = s.z / (s.z + s.w);\n\n    return mix(\n       mix(sample3, sample2, sx), mix(sample1, sample0, sx)\n    , sy);\n}"]));
        shaderHelper.addChunk("textureSmoothstep", glslify(["#define GLSLIFY 1\n// http://www.iquilezles.org/www/articles/texture/texture.htm\nvec4 textureSmoothstep(sampler2D t, vec2 texCoords, vec2 textureSize) {\n\ttexCoords = texCoords * textureSize + 0.5;\n\tvec2 iTexCoords = floor( texCoords );\n\tvec2 fTexCoords = fract( texCoords );\n\ttexCoords = iTexCoords + fTexCoords*fTexCoords*(3.0-2.0*fTexCoords);\n\ttexCoords = (texCoords - 0.5)/textureSize;\n\treturn texture2D( t, texCoords );\n}"]));


        this.resources = _options.resources
        this.niceFloorTexture = this.resources.items.floorTexture

        // Options
        this.debug = _options.debug

        // Container
        this.container = new THREE.Object3D()
        this.container.matrixAutoUpdate = false

        // Geometry
        this.geometry = new THREE.PlaneBufferGeometry(2, 2, 10, 10)

        // Colors
        this.colors = {}
        // this.colors.topLeft = '#f5883c'
        // this.colors.topRight = '#ff9043'
        // this.colors.bottomRight = '#fccf92'
        // this.colors.bottomLeft = '#f5aa58'

        this.colors.topLeft = '#4a3d3d'
        this.colors.topRight = '#261e1e'
        this.colors.bottomRight = '#3b2828'
        this.colors.bottomLeft = '#222222'

        // Material
        this.material = new FloorMaterial()

        this.updateMaterial = () =>
        {
            const topLeft = new THREE.Color(this.colors.topLeft)
            const topRight = new THREE.Color(this.colors.topRight)
            const bottomRight = new THREE.Color(this.colors.bottomRight)
            const bottomLeft = new THREE.Color(this.colors.bottomLeft)

            const data = new Uint8Array([
                Math.round(bottomLeft.r * 255), Math.round(bottomLeft.g * 255), Math.round(bottomLeft.b * 255),
                Math.round(bottomRight.r * 255), Math.round(bottomRight.g * 255), Math.round(bottomRight.b * 255),
                Math.round(topLeft.r * 255), Math.round(topLeft.g * 255), Math.round(topLeft.b * 255),
                Math.round(topRight.r * 255), Math.round(topRight.g * 255), Math.round(topRight.b * 255)
            ])

            this.backgroundTexture = new THREE.DataTexture(data, 2, 2, THREE.RGBFormat)
            this.backgroundTexture.magFilter = THREE.LinearFilter
            this.backgroundTexture.needsUpdate = true

            this.material.uniforms.tBackground.value = this.backgroundTexture
        }

        this.updateMaterial()

        // Mesh
        this.mesh = new THREE.Mesh(this.geometry, this.material)
        this.mesh.frustumCulled = false
        this.mesh.matrixAutoUpdate = false
        this.mesh.updateMatrix()
        this.container.add(this.mesh)

        // Debug
        if(this.debug)
        {
            const folder = this.debug.addFolder('floor')
            // folder.open()

            folder.addColor(this.colors, 'topLeft').onChange(this.updateMaterial)
            folder.addColor(this.colors, 'topRight').onChange(this.updateMaterial)
            folder.addColor(this.colors, 'bottomRight').onChange(this.updateMaterial)
            folder.addColor(this.colors, 'bottomLeft').onChange(this.updateMaterial)
        }

        this.setNiceFloor();
    }


    setNiceFloor()
    {
        this.niceFloor = {}



        // Container
        this.niceFloor.container = new THREE.Object3D()
        this.niceFloor.container.position.x = - 10
        this.niceFloor.container.position.y = + 10
        // this.niceFloor.container.position.z = -1
        this.niceFloor.container.matrixAutoUpdate = false
        this.niceFloor.container.updateMatrix()
        this.container.add(this.niceFloor.container)

        // Texture
        this.niceFloor.texture = this.niceFloorTexture
        this.niceFloor.texture.magFilter = THREE.NearestFilter
        this.niceFloor.texture.minFilter = THREE.LinearFilter

        this.niceFloor.texture.needsUpdate = true;
        this.niceFloor.texture.wrapS = this.niceFloor.texture.wrapT = THREE.RepeatWrapping;

        // Geometry
        this.niceFloor.geometry = new THREE.PlaneBufferGeometry(64, 64)

        // Material
        this.niceFloor.material =  new THREE.MeshBasicMaterial({ transparent: false, depthWrite: false, map: this.niceFloor.texture})

        // Mesh
        this.niceFloor.mesh = new THREE.Mesh(this.niceFloor.geometry, this.niceFloor.material)
        this.niceFloor.mesh.matrixAutoUpdate = false















        this.someVector2 = new THREE.Vector2;

        this.matrix = new THREE.Matrix4;
        /** @type {number} */
        this.someZeroNumber = 0;
        this.perspectiveCamera = new THREE.PerspectiveCamera;
        this.b = new THREE.Plane;
        this.v = new THREE.Vector3;
        this.p = new THREE.Vector3;
        this.vector = new THREE.Vector3;
        this.tempMatrix = new THREE.Matrix4;
        this.v1 = new THREE.Vector3;
        this.someOtherVector4 = new THREE.Vector4;
        this._vector3 = new THREE.Vector3;
        this.qVector = new THREE.Vector3;
        this.tVector = new THREE.Vector3;
        this.someVector4 = new THREE.Vector4;

        this.mipmapperInst = new mipmapper();
        this.renderTarget = fboHelper.createRenderTarget(1, 1)
        this.renderTarget.depthBuffer = true
        this.niceFloor.mesh = new THREE.Mesh(new THREE.PlaneBufferGeometry(30, 30), new THREE.ShaderMaterial({
            uniforms: {
                u_infoTexture: {
                    value: this.niceFloor.texture
                },
                u_textureMatrix: {
                    value: new THREE.Matrix4
                },
                u_textureSize: {
                    value: new THREE.Vector2
                },
                u_mipmapTextureSize: {
                    value: this.mipmapperInst.textureSize
                },
                u_mipmapTexture: {
                    value: this.renderTarget.texture
                },
                u_noiseTime: {
                    value: 0
                },
                ...blueNoise.sharedUniforms,
                ...glPositionOffset.sharedUniforms
            },
            vertexShader: glslify(["#define GLSLIFY 1\nuniform mat4 u_textureMatrix;\n\nvarying vec2 v_uv;\nvarying vec4 v_reflectionTextureUv;\n\n#include <glPositionOffset>\n\nvoid main () {\n\tv_uv = uv;\n\tv_reflectionTextureUv = u_textureMatrix * vec4( position, 1.0 );\n\n    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);\n\n    gl_Position = projectionMatrix * mvPosition;\n\t// gl_Position = glPositionOffset(gl_Position);\n}"]),
            fragmentShader: glslify(["#define GLSLIFY 1\nuniform sampler2D u_infoTexture;\n\nuniform sampler2D u_mipmapTexture;\nuniform vec2 u_textureSize;\nuniform vec2 u_mipmapTextureSize;\nuniform float u_noiseTime;\n\n#include <textureSmoothstep>\n#include <textureBicubic>\n#include <lodMipmapSample>\n\n#include <getBlueNoise>\n\nvarying vec4 v_reflectionTextureUv;\nvarying vec2 v_uv;\n\nvec2 mirrored(vec2 v) {\n    vec2 m = mod(v, 2.0);\n    return mix(m, 2.0 - m, step(1.0, m));\n}\n\nvoid main () {\n\tvec3 blueNoise = getBlueNoise(gl_FragCoord.xy);\n\t\n\tvec2 reflectionUv = v_reflectionTextureUv.xy / v_reflectionTextureUv.w + blueNoise.xy / u_mipmapTextureSize;\n    vec3 color = lodMipmapSampleNearest(u_mipmapTexture, reflectionUv, 0.0, u_textureSize, u_mipmapTextureSize).rgb;\n\t// vec3 info = texture2D(u_infoTexture, v_uv * 6.0).rgb;\n\tvec3 info;\n\tvec3 blury;\n\t#ifdef IS_MOBILE\n\tinfo = textureSmoothstep(u_infoTexture, v_uv * 6.0, vec2(1024.0)).rgb;\n    blury = lodMipmapSampleSmoothstep(u_mipmapTexture, reflectionUv + info.br / 128.0, info.r * info.g * 15.0, u_textureSize, u_mipmapTextureSize).rgb;\n\tcolor = mix(color, blury, clamp((info.g - 0.25) * 5.0, 0.0, 1.0));\n\t\n\tinfo = textureBicubic(u_infoTexture, v_uv * 2.0 + 0.421, vec2(1024.0)).rgb;\n    blury = lodMipmapSampleSmoothstep(u_mipmapTexture, reflectionUv, info.r * 6.0, u_textureSize, u_mipmapTextureSize).rgb;\n\tcolor = mix(color, blury, clamp((info.b + blueNoise.z * 0.05 - 0.3) *  7.0, 0.0, 1.0));\n\t#else\n\tinfo = textureBicubic(u_infoTexture, v_uv * 6.0, vec2(1024.0)).rgb;\n    blury = lodMipmapSampleCubic(u_mipmapTexture, reflectionUv + info.br / 128.0, info.r * info.g * 15.0, u_textureSize, u_mipmapTextureSize).rgb;\n\tcolor = mix(color, blury, clamp((info.g - 0.25) * 5.0, 0.0, 1.0));\n\t\n\tinfo = textureBicubic(u_infoTexture, v_uv * 2.0 + 0.421, vec2(1024.0)).rgb;\n    blury = lodMipmapSampleCubic(u_mipmapTexture, reflectionUv, info.r * 6.0, u_textureSize, u_mipmapTextureSize).rgb;\n\tcolor = mix(color, blury, clamp((info.b + blueNoise.z * 0.05 - 0.3) *  7.0, 0.0, 1.0));\n\t#endif\n\n\tcolor *= mix(0.6, 0.75, info.g);\n\n\tgl_FragColor = vec4(color + blueNoise.rgb / 128.0, 1.0);\n}"])
        }));

        this.niceFloor.mesh.onBeforeRender = this.update;


        this.niceFloor.container.add(this.niceFloor.mesh)


    }

    update(renderer, input, camera) {
        if (!this.someVector2) {
            return;
        }
            this.p.setFromMatrixPosition(this.niceFloor.mesh.matrixWorld);
            this.vector.setFromMatrixPosition(camera.matrixWorld);
            this.tempMatrix.extractRotation(this.niceFloor.mesh.matrixWorld);
            this.v.set(0, 0, 1);
            this.v.applyMatrix4(this.tempMatrix);
            this.qVector.subVectors(this.p, this.vector)


            if (!(0 < this.qVector.dot(this.v))) {
                this.qVector.reflect(this.v).negate();
                this.qVector.add(this.p);
                this.tempMatrix.extractRotation(camera.matrixWorld);
                this.v1.set(0, 0, -1);
                this.v1.applyMatrix4(this.tempMatrix);
                this.v1.add(this.vector);
                this.tVector.subVectors(this.p, this.v1);
                this.tVector.reflect(this.v).negate();
                this.tVector.add(this.p);
                this.perspectiveCamera.position.copy(this.qVector);
                this.perspectiveCamera.up.set(0, 1, 0);
                this.perspectiveCamera.up.applyMatrix4(this.tempMatrix);
                this.perspectiveCamera.up.reflect(this.v);
                this.perspectiveCamera.lookAt(this.tVector);
                this.perspectiveCamera.near = camera.near;
                this.perspectiveCamera.far = camera.far;
                this.perspectiveCamera.updateMatrixWorld();
                this.perspectiveCamera.projectionMatrix.copy(camera.projectionMatrix);
                this.matrix.set(.5, 0, 0, .5, 0, .5, 0, .5, 0, 0, .5, .5, 0, 0, 0, 1);
                this.matrix.multiply(this.perspectiveCamera.projectionMatrix);
                this.matrix.multiply(this.perspectiveCamera.matrixWorldInverse);
                this.matrix.multiply(this.niceFloor.mesh.matrixWorld);
                this.b.setFromNormalAndCoplanarPoint(this.v, this.p);
                this.b.applyMatrix4(this.perspectiveCamera.matrixWorldInverse);
                this.someOtherVector4.set(this.b.normal.x, this.b.normal.y, this.b.normal.z, this.b.constant);
                var m = this.perspectiveCamera.projectionMatrix;
                /** @type {number} */
                this.someVector4.x = (Math.sign(this.someOtherVector4.x) + m.elements[8]) / m.elements[0];
                /** @type {number} */
                this.someVector4.y = (Math.sign(this.someOtherVector4.y) + m.elements[9]) / m.elements[5];
                /** @type {number} */
                this.someVector4.z = -1;
                /** @type {number} */
                this.someVector4.w = (1 + m.elements[10]) / m.elements[14];
                this.someOtherVector4.multiplyScalar(2 / this.someOtherVector4.dot(this.someVector4));
                m.elements[2] = this.someOtherVector4.x;
                m.elements[6] = this.someOtherVector4.y;
                /** @type {number} */
                m.elements[10] = this.someOtherVector4.z + 1 - this.someZeroNumber;
                m.elements[14] = this.someOtherVector4.w;
                this._vector3.setFromMatrixPosition(camera.matrixWorld);
                /** @type {boolean} */
                // renderer.xr.enabled = false;
                /** @type {boolean} */
                this.niceFloor.mesh.visible = false;
                var textureTarget = renderer.getRenderTarget();
                /** @type {boolean} */
                // var tmpEnabled = renderer.xr.enabled;
                /** @type {boolean} */
                var currentSceneAutoUpdate = renderer.shadowMap.autoUpdate;
                /** @type {boolean} */
                // renderer.xr.enabled = false;
                /** @type {boolean} */
                renderer.shadowMap.autoUpdate = false;
                renderer.setRenderTarget(this.renderTarget);
                renderer.setViewport(0, 0, this.someVector2.x, this.someVector2.y);
                renderer.setScissor(0, 0, this.someVector2.x, this.someVector2.y);
                renderer.setScissorTest(true);
                renderer.render(input, this.perspectiveCamera);
                renderer.setRenderTarget(null);

                var renderRectL = {
                    width: 1000,
                    height: 1000
                }
                renderer.setViewport(0, 0, renderRectL.width, renderRectL.height);
                renderer.setScissor(0, 0, renderRectL.width, renderRectL.height);
                this.mipmapperInst.render(this.renderTarget);
                /** @type {boolean} */
                // renderer.xr.enabled = tmpEnabled;
                /** @type {boolean} */
                renderer.shadowMap.autoUpdate = currentSceneAutoUpdate;
                renderer.setRenderTarget(textureTarget);
                var width = camera.viewport;
                if (void 0 !== width) {
                    renderer.state.viewport(width);
                }
                /** @type {boolean} */
                this.niceFloor.mesh.visible = true;
                this.niceFloor.mesh.material.uniforms.u_noiseTime.value += .01;

            }
    }

    resize(b, d) {
        if (!this.someVector2) {
            return;
        }
        /** @type {number} */
        // var a = platform.isMobile ? .5 : 1;
        var a = 1;
        /** @type {number} */
        b = b * a;
        /** @type {number} */
        d = d * a;
        this.someVector2.set(b, d);
        this.renderTarget.setSize(1.5 * this.someVector2.x, this.someVector2.y);
        this.mipmapperInst.resize(this.someVector2.x, this.someVector2.y, this.renderTarget.width, this.renderTarget.height);
    }

}
